import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { Component, OnInit, Input, Inject, EventEmitter, Output } from '@angular/core';
import { BaseOption } from '@shared/models/base-option';
import { Observable } from 'rxjs';
import { Language } from '@core/modules/language/models';
import { first } from 'rxjs/operators';
import { UserConfig, USER_CONFIG } from '../../user.config';
import { BasicInformation, User } from '../../models';
import { BasicInformationFormControlName } from './models/basic-information-form-control-name.enum';

@Component({
    selector: 'app-basic-information-form',
    templateUrl: './basic-information-form.component.html',
    styleUrls: ['./basic-information-form.component.scss'],
    standalone: false
})
export class BasicInformationFormComponent implements OnInit {
  @Input() label!: string;
  @Input() readonlyLocation!: boolean;
  @Input() user!: Observable<User | undefined>;
  @Input() languages!: Observable<Language[]>;
  @Input() countries!: Observable<BaseOption[]>;

  @Output() basicInformationSubmit = new EventEmitter<BasicInformation>();

  form!: FormGroup;
  isSubmitted!: boolean;
  errorFeedbackList!: { [property: string]: { [validation: string]: string } };

  get countryControl() {
    return <FormControl>this.form.get(BasicInformationFormControlName.COUNTRY_ID);
  }

  get langControl() {
    return <FormControl>this.form.get(BasicInformationFormControlName.LANG_ID)!;
  }

  get avatarControl() {
    return <FormControl>this.form.get(BasicInformationFormControlName.AVATAR)!;
  }

  get firstNameControl() {
    return <FormControl>this.form.get(BasicInformationFormControlName.FIRST_NAME)!;
  }

  get lastNameControl() {
    return <FormControl>this.form.get(BasicInformationFormControlName.LAST_NAME)!;
  }

  get cityControl() {
    return <FormControl>this.form.get(BasicInformationFormControlName.CITY)!;
  }

  constructor(private fb: FormBuilder, @Inject(USER_CONFIG) private config: UserConfig) {}

  ngOnInit(): void {
    this.isSubmitted = false;
    this.errorFeedbackList = this.getFeedbackErrorLists();
    this.user.pipe(first()).subscribe((user) => (this.form = this.initForm(user)));
  }

  onSubmit(): void {
    this.isSubmitted = true;
    this.form.valid &&
      this.basicInformationSubmit.emit({
        ...this.form.value,
        [BasicInformationFormControlName.COUNTRY_ID]: Number(this.countryControl.value),
        [BasicInformationFormControlName.LANG_ID]: Number(this.langControl.value)
      });
  }

  onImageChange(imageBase64: string) {
    this.avatarControl.setValue(imageBase64);
  }

  private initForm(user: User | undefined) {
    return this.fb.group({
      [BasicInformationFormControlName.FIRST_NAME]: [user?.firstName, [Validators.required]],
      [BasicInformationFormControlName.LAST_NAME]: [user?.lastName, [Validators.required]],
      [BasicInformationFormControlName.AVATAR]: [user?.avatar],
      [BasicInformationFormControlName.COUNTRY_ID]: [user?.countryId, [Validators.required]],
      [BasicInformationFormControlName.CITY]: [user?.city, [Validators.required]],
      [BasicInformationFormControlName.LANG_ID]: [user?.langId, [Validators.required]]
    });
  }

  private getFeedbackErrorLists(): {
    [property: string]: { [validation: string]: string };
  } {
    return {
      [BasicInformationFormControlName.FIRST_NAME]: this.config.ERRORS.REQUIRED_FIELD,
      [BasicInformationFormControlName.LAST_NAME]: this.config.ERRORS.REQUIRED_FIELD,
      username: this.config.ERRORS.REQUIRED_FIELD,
      [BasicInformationFormControlName.COUNTRY_ID]: this.config.ERRORS.REQUIRED_FIELD,
      [BasicInformationFormControlName.CITY]: this.config.ERRORS.REQUIRED_FIELD,
      [BasicInformationFormControlName.LANG_ID]: this.config.ERRORS.REQUIRED_FIELD
    };
  }
}
